<template>
  <div class="mb-6 border-b divide-balternative-color-200">
    <div class="grid grid-rows md:grid-cols md:grid-rows-1 md:grid-cols-2">
      <div class="mb-2 md:mb-0">
        <div class="flex items-center flex-wrap">
          <span class="text-lg text-talternative-color font-bold">
            {{ formattedTherapyNames }}
          </span>
          <span
            class="relative rounded px-1 text-xs text-success-default mx-2 border border-success-default font-semibold"
            v-if="data.status === 'active'"
          >
            Actief
          </span>
          <span
            class="relative rounded px-1 text-xs text-warning-default mx-2 border border-warning-default font-semibold"
            v-else-if="data.status === 'almostExpired'"
          >
            Bijna verlopen
          </span>
          <span
            class="relative rounded px-1 text-xs text-danger-default mx-2 border border-danger-default font-semibold"
            v-else
          >
            Verlopen
          </span>
          <span
            class="relative rounded px-1 text-xs text-primary-default border border-primary-default font-semibold"
            v-if="data.trialLicense"
          >
            Proefplaatsing
          </span>
        </div>
        <div class="flex items-center text-sm text-secondary-text mt-1 flex-wrap">
          <span>
            {{ formattedIllnessDescription }}
          </span>
          <span class="mx-1">-</span>
          <span>Aangemaakt door: {{ data.prescriber }}</span>
        </div>
        <div class="flex items-center text-sm text-secondary-text mt-1 flex-wrap">
          <span>Startdatum: {{ $formatDate(data.startDate) }} </span>
          <span v-if="data.endDate" class="mx-1">|</span>
          <span v-if="data.endDate">Einddatum: {{ $formatDate(data.endDate) }}</span>
        </div>
        <div v-if="data.requireEquipmentAssignment && !data.equipments.length" class="flex z-40 w-auto my-2">
          <Alert
            :html="true"
            :htmlMessage="pumpAssignMessage"
            type="alert"
            @custom-link-click="assignPump()"
            custom-link="Pomp toewijzen"
            :custom-icon="customIcon"
          ></Alert>
        </div>
        <div class="flex z-40 w-auto my-2" v-if="data.equipments.length">
          <Alert
            :html="true"
            :htmlMessage="pumpChangeMessage"
            type="info"
            @custom-link-click="
              $emit('exchange', { id: data.equipments[0].equipmentId, licenseId: data.licenses[0].id })
            "
            custom-link="Pomp wisselen"
            :custom-icon="customIcon"
          ></Alert>
        </div>
      </div>
      <div class="justify-items-end place-content-center space-x-2">
        <div class="flex justify-end place-content-end items-start md:items-end self-start flex-wrap flex-row">
          <!-- restart button //-->
          <button
            @click.prevent="editLicense()"
            v-if="data.isRestartable"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <PencilIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">Herstarten</span>
          </button>

          <!-- Edit button //-->
          <button
            @click.prevent="editLicense()"
            v-if="data.isEditable && !isArchived && data.isExtendable"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <PencilIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">{{ getIsEditableButtonText() }}</span>
          </button>

          <!-- Prolong button //-->
          <button
            @click.prevent="extendLicense()"
            v-if="data.isNutritionLicense && data.isActive && !data.isQuestionSetChanged"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <ArrowUturnRightIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">Verlengen</span>
          </button>

          <!-- Stop button //-->
          <button
            @click.prevent="
              $emit('stop', { licenseData: data, licenseId: data.licenses[0].id, equipments: data.equipments })
            "
            v-if="data.isStoppable && !data.hideStopLicenseButton"
            :class="[{ 'opacity-50 cursor-not-allowed': data.hideStopLicenseButton }]"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <XCircleIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">Stopzetten</span>
          </button>

          <!-- Documenten button //-->
          <button
            @click.prevent="loadPdfDetails()"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <ArrowDownTrayIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">Documenten</span>
          </button>

          <!-- OSA / POSA related buttons //-->
          <button
            v-if="data.isApproved"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 opacity-50 cursor-not-allowed mb-2 mr-2"
          >
            <span class="">Akkoordverklaring verzonden</span>
          </button>

          <button
            @click.prevent="goToApproval()"
            v-if="data.evaluationType && data.isApproved !== true"
            class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-xs md:text-sm lg:text-sm font-medium text-white bg-primary-default border border-primary-default rounded-full hover:bg-primary-default/50 mb-2 mr-2"
          >
            <span class="">Rond de Akkoordverklaring af</span>
          </button>

          <button
            @click.prevent="goToMedication()"
            v-if="data.showMedication"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <v-pill-icon class="inline-block mr-1 fill-primary-color" />
            <span class="">Medicatiewijziging doorgeven</span>
          </button>

          <button
            @click.prevent="goToDelivery()"
            v-if="data.showDeliveryNote"
            class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2"
          >
            <StopIcon class="h-3 md:h-4 lg:h-4 w-3 md:w-4 lg:w-4 inline-block mr-1" aria-hidden="true" />
            <span class="">Afleverbon aanmaken</span>
          </button>
        </div>
      </div>
    </div>
    <div class="grid grid-rows mb-2" v-if="isShowOsaSectionFeatureEnabled && data.licenses[0].isOsa">
       <div class="flex grid grid-cols-2">
        <div class="text-lg text-talternative-color font-bold self-center">Meet-en contactmomenten</div>
        <div class="flex m-2 place-content-end">
          <v-button-dropdown
            :options="osaOptions"
            :button-props="{ hollow: true, inverted: true, alt: true, md: true, iconLeft: true }"
            @on-select="redirectToOsaPage"
          >
           Therapie informatie
          </v-button-dropdown>
        </div>
      </div>
      <div class="overflow-hidden" v-if="showOsaBlock">
        <tables class="overflow-hidden border-t border-balternative-color">
          <template #tableHeaders>
            <thead>
              <tr class="bg-sky-50 border-x border-b border-balternative-color">
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                  v-for="header in osaHeader"
                  :key="header"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
          </template>
          <template #tableBody>
            <tbody class="bg-white">
              <v-osa-details-row
                v-for="(row, index) in computedOsaDetails"
                :key="index"
                :data="row"
                :is-last="index === data.licenses[0].osaEvaluation.length - 1"
                :is-posa="isPosa"
              />
            </tbody>
          </template>
        </tables>
      </div>
      <div class="overflow-hidden" v-if="contactDetails && contactDetails.length > 0">
        <tables class="overflow-hidden border-t border-balternative-color">
          <template #tableHeaders>
            <thead>
              <tr class="bg-sky-50 border-x border-b border-balternative-color">
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                  v-for="header in osaContactHeader"
                  :key="header"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
          </template>
          <template #tableBody>
            <tbody class="bg-white">
              <v-contact-details-row
                v-for="(row, index) in contactDetails"
                :key="index"
                :data="row"
              />
            </tbody>
          </template>
        </tables>
      </div>
    </div>
    <!--Pop up to show extend license -->
    <generic-modal v-if="showExtendLicensePopUp" @close="closeModal">
      <template #header> Machtiging verlengen </template>
      <template #body>
        <h4>Machtiging verlergen van {{ $formatDate(data.endDate) }} naar:</h4>
        <div class="license__extnDate">
          <v-input-date-picker
            id="extnDate"
            ref="date"
            type="date"
            name="extnDate"
            autofocus
            :value="customStartDate"
            :custom-start-date="customStartDate"
            @update-validation="updateValidation"
          />
        </div>
      </template>
      <template #footer>
        <v-button tagelement="button" hollow md primary @click="closeModal()"> Annuleren </v-button>
        <v-button tagelement="button" md primary @click="extLicense()"> Bevestigen </v-button>
      </template>
    </generic-modal>
    <!--Pop up to show pdf details -->
    <generic-modal v-if="showPdfDetailsPopUp" :has-close-button="true" @close="closeModal" :modal-Medium-Width="true">
      <template #body>
        <div class="grid grid-cols-2 mb-4">
          <div class="font-bold text-2xl justify-start content-center ml-2">Documenten</div>
          <div class="flex justify-end" v-if="!showUploadedFiles">
            <button
              @click.prevent="openCompactFileUpload"
              class="flex justify-center py-2 px-2 md:px-4 border border-primary-default shadow-sm text-sm font-medium text-primary-default bg-white rounded-full hover:bg-primary-default/50 items-center"
            >
              Document toevoegen
            </button>
          </div>
        </div>
        <div v-show="showUploadedFiles" class="p-0">
          <v-input-file-upload-compact
            :id="'file' + data.accountabilityId"
            :uploaded-files-data="licensePdfDetails"
            :accountability-upload="true"
            :allowed-extenstions="allowedFileExtenstions"
            :drop-area-secondary-message="dropAreaSecondaryMessage"
            :extension-error-message="extensionErrorMessage"
            :size-error-message="sizeErrorMessage"
            :maximum-file-size="maximumFileSize"
            :image-maximum-size="maximumFileSize"
            @file-added="showUploadedFileSection"
            @upload-success="saveUploadedFiles"
          />
        </div>
        <div class="overflow-y-auto h-72 overflow-x-hidden">
          <tables id="licensePdfDetails">
            <template #tableHeaders>
              <thead>
                <tr class="bg-sky-50">
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                    v-for="(header, index) in pdfTableHeaders"
                    :key="header"
                    :class="{ 'w-4/12': index === 0 }"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
            </template>
            <template #tableBody>
              <tbody class="bg-white divide-y divide-sky-200">
                <tr v-for="(item, index) in licensePdfDetails" :key="index">
                  <td class="py-3.5 pl-4 pr-3 text-left text-base text-pdsPrimaryColor">{{ item.documentType }}</td>
                  <td
                    class="py-3.5 pl-4 pr-3 text-left text-base text-pdsPrimaryColor underline cursor-pointer font-bold"
                    @click="downloadLicensePdf(item)"
                  >
                    {{ item.fileName }}
                    <span v-if="item.creationDate"> - {{ moment(item.creationDate).format('HHmmss') }}</span>
                    <span v-else-if="item.uploadDate"> - {{ moment(item.uploadDate).format('HHmmss') }}</span>
                  </td>
                  <td
                    class="py-3.5 pl-2 pr-3 text-left text-base text-pdsPrimaryColor text-left min-w-28"
                    :class="{ 'col-span-2': !item.isDeletable }"
                  >
                    <span v-if="item.creationDate">{{ $formatDate(item.creationDate) }}</span>
                    <span v-else-if="item.uploadDate">{{ $formatDate(item.uploadDate) }}</span>
                  </td>
                  <td
                    class="py-3.5 pl-4 pr-3 text-left text-base text-pdsPrimaryColor text-left"
                    v-if="item.isDeletable"
                  >
                    <TrashIcon
                      class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block mr-1"
                      aria-hidden="true"
                      v-tooltip="{ content: 'Document verwijderen', placement: 'left', html: false }"
                      @click.prevent="deleteAccountabilityPdf(item)"
                    />
                  </td>
                </tr>
                <tr v-if="!licensePdfDetails || (licensePdfDetails && licensePdfDetails.length === 0)">
                  <td colspan="4" class="py-3.5 pl-4 pr-3 text-left text-base text-pdsPrimaryColor">
                    Nog geen documenten toegevoegd. Voeg hier uw document toe om de machtiging compleet te maken.
                  </td>
                </tr>
              </tbody>
            </template>
          </tables>
        </div>
      </template>
      <template #footer>
        <v-button tagelement="button" hollow md transparent primary @click="closeModal()"> Sluiten </v-button>
      </template>
    </generic-modal>
  </div>
</template>

<script>
import shared from '@/components/shared/shared';
import router from '@/router';
import VInputFileUploadCompact from '@/components/shared/form/simple/InputFileUploadCompact';
import moment from 'moment';
import VInputDatePicker from '@/components/shared/form/simple/InputDatePicker';
import { localDateToJson, stringToLocalDate } from '@/services/date.service';
import VPillIcon from 'icons/Pill';
import GenericModal from '@/components/shared/GenericModal';
import axios from 'axios';
import { invalidateLicenseOverviews$ } from '@/services';
import { computed, ref, markRaw } from 'vue';
import { useStore, mapActions } from 'vuex';
import {
  PencilIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  ArrowUturnRightIcon,
  ArrowRightIcon
} from '@heroicons/vue/24/outline';
import { StopIcon, TrashIcon } from '@heroicons/vue/20/solid';
import VButtonDropdown from '@/components/shared/ButtonDropdown';
import VOsaDetailsRow from '@/components/sections/osa/osaDetailsRow';
import VContactDetailsRow from '@/components/sections/osa/ContactDetailsRow';

export default {
  name: 'LicenseRow',
  components: {
    VInputFileUploadCompact,
    VInputDatePicker,
    VPillIcon,
    GenericModal,
    PencilIcon,
    XCircleIcon,
    ArrowDownTrayIcon,
    ArrowUturnRightIcon,
    StopIcon,
    VButtonDropdown,
    VOsaDetailsRow,
    VContactDetailsRow,
    TrashIcon
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    pumpRetrieveButton: {
      type: Boolean
    },
    isArchived: {
      type: Boolean
    },
    isShowOsaSectionFeatureEnabled: {
      type: Boolean
    }
  },
  setup(props) {
    const store = useStore();
    const osaOptions = ref([
      { id: 0, title: 'Evaluatie (P)OSA' },
      { id: 1, title: 'Contactmoment' }
    ]);
    const contactDetails = ref([]);
    const siteCode = computed(() => store.getters['navigation/getSiteCode']);
    const patientGegevens = computed(() => store.getters['patient/getPatientGegevens']);
    const setLicenseStopPopUpStatus = (value) => store.commit('accountability/setLicenseStopPopUpStatus', value);
    const saveStandAloneAccountability = (value) =>
      store.dispatch('accountability/saveStandAloneAccountability', value);
    const saveAccountabilityFiles = (value) => store.dispatch('accountability/saveAccountabilityFiles', value);
    const customIcon = markRaw(ArrowRightIcon);
    const pumpAssignMessage = ref(
      '<div class="text-tdefault-color">Let op! Er is nog geen pomp toegewezen aan deze machtiging</div>'
    );
    const isPosa = computed(() => {
      return props.data.licenses[0] && props.data.licenses[0].illnessId === '1061';
    });
    const osaHeader = computed(() => {
      return ['Datum invoer', 'Type', 'Stemming patiënt', ...(!isPosa.value ? ['AHI'] : []), 'Oordeel patiënt', ''];
    });
    const osaContactHeader = computed(() => {
      return ['Datum', 'Type', 'Toegevoegd door', 'Opmerkingen','', '' ];
    });
    const pumpChangeMessage = computed(() => {
      const equipment = props.data?.equipments?.[0];
      if (!equipment) {
        return '';
      }
      const dateText = equipment.dateOfAllocation
        ? ` op ${moment(equipment.dateOfAllocation).format('DD-MM-YYYY')}`
        : '';

      return `<div class="text-tdefault-color flex">
          ${equipment.description} (Serienummer: ${equipment.serialNumber})
          <br/>
          ${equipment.pickUp?.pumpPickupText} Pomp toegewezen door ${equipment.prescriberName}${dateText}
        </div>`;
    });

    const formattedTherapyNames = computed(() => {
      return props.data.licenses.map((license) => license.therapyName).join(' | ');
    });
    const formattedIllnessDescription = computed(() => {
      return props.data.licenses.map((license) => license.illnessDescription).join(' | ');
    });
    const showOsaBlock = computed(() => {
      return (
        props.data.licenses[0].isOsa &&
        (props.data.licenses[0].osaEvaluation.length !== 0 || props.data.licenses[0].osaReadout.length !== 0)
      );
    });

    const computedOsaDetails = computed(() => {
      const result = [];

      const { osaEvaluation, osaReadout } = props.data.licenses[0];

      if (osaEvaluation.length > 0) {
        result.push(...osaEvaluation);
      }
      if (osaReadout.length > 0) {
        result.push(...osaReadout);
      }
      return result;
    });
    const redirectToOsaPage = (osaType) => {
      if (osaType === 0 || osaType === 2) {
        router.push('/osa/evaluatie');
      }
      if (osaType === 1) {
      const licenseId = props.data.licenses[0].id;
        router.push('/osa/contact/' + licenseId);
      }
    };
      const fetchContactRowOverview = async () => {
      const response = await axios.get('Osa/GetOsaContactDetail?licenseId=' + props.data.licenses[0].id).then((response) => {
        if (response && response.data.ContactOverview) {
          contactDetails.value = response.data.ContactOverview;
        }
      });
    };
    fetchContactRowOverview();
    const sortByDates = (pdfDetails) => {
      return pdfDetails.sort((x, y) => {
        const getLatest = (obj) => {
          let dates = [obj.creationDate, obj.uploadDate].filter((d) => d !== null).map((d) => new Date(d));

          return dates.length ? Math.max(...dates) : null;
        };

        const xDate = getLatest(x);
        const yDate = getLatest(y);

        // Case 1: both null → equal (stay grouped at top)
        if (xDate === null && yDate === null) return 0;

        // Case 2: x null → x should come first
        if (xDate === null) return -1;
        if (yDate === null) return 1;

        // Case 3: both have dates → descending order
        return yDate - xDate;
      });
    };

    return {
      siteCode,
      patientGegevens,
      formattedTherapyNames,
      formattedIllnessDescription,
      setLicenseStopPopUpStatus,
      saveStandAloneAccountability,
      saveAccountabilityFiles,
      ...mapActions({
        requestAccountabilityCopy: 'accountability/requestAccountabilityCopy'
      }),
      pumpAssignMessage,
      pumpChangeMessage,
      customIcon,
      osaOptions,
      redirectToOsaPage,
      osaHeader,
      osaContactHeader,
      showOsaBlock,
      computedOsaDetails,
      isPosa,
      sortByDates,
      contactDetails
    };
  },
  data() {
    return {
      beforeExpanded: false,
      isTransitioning: false,
      rowHeight: 'auto',
      rowHeightCollapsed: 'auto',
      showExtendLicensePopUp: false,
      isDateValidated: false,
      extnDate: null,
      showPdfDetailsPopUp: false,
      customStartDate: null,
      formData: '',
      osaType: 'Evaluation',
      pdfTableHeaders: ['Document Type', 'Omschrijving', 'Datum', ''],
      showUploadedFiles: false,
      licensePdfDetails: [],
      allowedFileExtenstions: ['pdf', 'jpg', 'jpeg', 'png'],
      maximumFileSize: 7,
      dropAreaSecondaryMessage: '(max. 7MB; alleen .pdf, .jpeg, .jpg of .png)',
      extensionErrorMessage:
        'Het geüploade document heeft niet de juiste extensie. U kunt alleen PDF, JPEG, JPG, of PNG documenten uploaden.',
      sizeErrorMessage: 'Het geüploade document is te groot. Het document mag niet groter zijn dan 7MB.'
    };
  },
  methods: {
    deleteAccountabilityPdf(item) {
      this.showPdfDetailsPopUp = false;
      // Start a promise, call for a prompt
      new Promise((resolve, reject) => {
        this.$emitter.emit('prompt', {
          title: 'Documenten verwijderen',
          message: 'U staat op het punt om document ' + item.fileName + ' definitief te verwijderen. Weet u het zeker?',
          resolve,
          reject
        });
      })
        .then((payload) => {
          // When prompt was confirmed
          axios
            .delete('Accountability/DeleteAccountabilityDocument', {
              params: {
                documentGUID: item.documentGUID,
                documentUUID: item.documentUUID
              }
            })
            .then((response) => {
              if (response && response.data) {
                this.$emitter.emit('alert', {
                  message: 'Document is succesvol verwijderd.',
                  type: 0
                });
                this.loadPdfDetails();
                this.showPdfDetailsPopUp = true;
              }
            })
            .catch(() => {
              this.$emitter.emit('alert', {
                message: 'Het verwijderen van dit document is niet gelukt. Probeer het later nogmaals.',
                type: 3
              });
              this.showPdfDetailsPopUp = true;
            });
        })
        .catch(() => {
          // When cancelled
          this.showPdfDetailsPopUp = true;
        });
    },
    openCompactFileUpload() {
      document.getElementById('file' + this.data.accountabilityId).click();
    },
    showUploadedFileSection(value) {
      this.showUploadedFiles = value;
    },
    closeModal() {
      this.showExtendLicensePopUp = false;
      this.showPdfDetailsPopUp = false;
    },
    fileExtention: shared.fileExtention,
    moment(...args) {
      return moment(...args);
    },
    saveUploadedFiles(fmData, licenceId, filetype) {
      let fileDetails = {
        licenseId: this.data.licenses[0].id,
        accountabilityId: this.data.accountabilityId,
        documentType: filetype,
        formData: fmData
      };
      this.saveAccountabilityFiles(fileDetails)
        .then((response) => {
          invalidateLicenseOverviews$();
          this.loadPdfDetails();
        })
        .catch((error) => {});
    },
    editLicense() {
      if (this.data.accountabilityId !== null) {
        this.$emit('edit', { id: this.data.accountabilityId });
        return;
      }
      this.setLicenseStopPopUpStatus(true);
      router.push('/nieuwemachtiging');
    },
    extendLicense() {
      if (this.data.accountabilityId !== null) {
        this.$emit('extend', { id: this.data.accountabilityId });
        return;
      }
      this.setLicenseStopPopUpStatus(true);
      router.push('/nieuwemachtiging');
    },
    extLicense() {
      const endDate = stringToLocalDate(this.extnDate);
      const endDateJson = localDateToJson(endDate);

      if (this.data.accountabilityId !== 0) {
        this.requestAccountabilityCopy({
          AccountabilityId: this.data.accountabilityId,
          IsAccountabilityUsedForAssigningMultiplePumps: false
        }).then((response) => {
          const copyAccountabilityId = response.data.Id;
          const startDate = new Date();
          const startDateJson = localDateToJson(startDate);

          this.startDate = moment(startDate).format('DD-MM-YYYY');
          const extLicenseDateDetails = {
            accountabilityId: copyAccountabilityId,
            startDate: startDateJson,
            endDate: endDateJson
          };
          this.saveStandAloneAccountability(extLicenseDateDetails).then(() => {
            this.showExtendLicensePopUp = false;
            this.$emitter.emit('alert', {
              message: 'Uw machtiging is met succes verlengt'
            });
            invalidateLicenseOverviews$();
          });
        });
      }
    },
    // Function to update the date value to stop equipment
    updateValidation(date) {
      this.isDateValidated = true;
      this.extnDate = date;
    },
    // Button text based on the portal site code
    getIsEditableButtonText() {
      if (this.data.isNutritionLicense) {
        return 'Wijzigen';
      } else if (this.data.endDate !== null) {
        return 'Wijzigen/verlengen';
      }
      return 'Wijzigen';
    },
    /**
     * @function goToApproval - Function to redirect prescriber to approval form
     */
    goToApproval() {
      let osaId = this.data.evaluationId;
      const type = this.data.evaluationType;
      const licenseId = this.data.licenses[0].id;
      if (type === this.osaType) {
        if (osaId === null) {
          osaId = 0;
        }
        router.push('/osa/akkoordverklaring/' + type + '/' + osaId + '/' + licenseId);
      }
    },
    /**
     * @function goToMedication - Function to redirect prescriber to the medication form
     */
    goToMedication() {
      router.push('/verneveling/medicatiewijziging/' + this.data.licenses[0].id);
    },
    /**
     * @function goToDelivery - Function to redirect prescriber to delivery form
     */
    goToDelivery() {
      const licenseId = this.data.licenses[0].id;
      router.push('/osa/afleverbon/' + licenseId);
    },
    assignPump() {
      const licenseId = this.data.licenses[0].id;
      this.$emit('assignPump', licenseId);
    },
    loadPdfDetails() {
      axios
        .post('Accountability/GetAllAccountabilityDocuments', {
          licenseId: this.data.licenses[0].id,
          licenseExternalId: this.data.licenses[0].externalId,
          accountabilityId: this.data.licenses[0].accountabilityId,
          metaOnly: true
        })
        .then((response) => {
          if (response && response.data) {
            this.licensePdfDetails = this.sortByDates(response.data);
            this.showPdfDetailsPopUp = true;
          }
        });
    },
    async downloadLicensePdf(item) {
      if (item.document !== null) {
        this.downloadPdf({
          fileName: item.fileName,
          content: item.document
        });
        return;
      }
      axios
        .post('Accountability/GetSingleAccountabilityDocument', {
          documentGUID: item.documentGUID
        })
        .then((response) => {
          if (response && response.data) {
            this.downloadPdf({
              fileName: response.data.fileName,
              content: response.data.document
            });
          }
        });
    },
    downloadPdf(details) {
      const linkSource = `data:application/pdf;base64,${details.content}`;
      const downloadLink = document.createElement('a');
      const fileName = details.fileName;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
      downloadLink.remove();
    }
  }
};
</script>
<style lang="scss" scoped>
.material-design-icon {
  :deep(svg) {
    fill: $teal-darkest !important;
    height: 20px !important;
    width: 20px !important;
  }
}
</style>
